
import { WaybillBlankBatch, WaybillBlank, BlankStatus } from '../../types';
import { createRepo } from '../repo';
import { DB_KEYS } from '../dbKeys';
import { auditBusiness } from '../auditBusiness';
import { waybillRepository } from '../repositories/waybillRepository';

const batchRepo = createRepo<WaybillBlankBatch>(DB_KEYS.WAYBILL_BLANK_BATCHES);
const blankRepo = createRepo<WaybillBlank>(DB_KEYS.WAYBILL_BLANKS);

export const getBlankBatches = async () => (await batchRepo.list({ pageSize: 1000 })).data;
export const createBlankBatch = async (item: Omit<WaybillBlankBatch, 'id' | 'status'>) => {
    // Validate uniqueness
    const allBlanks = (await blankRepo.list({ pageSize: 50000 })).data;
    const duplicate = allBlanks.find(b =>
        b.organizationId === item.organizationId &&
        b.series.toLowerCase() === item.series.toLowerCase() &&
        b.number >= item.startNumber &&
        b.number <= item.endNumber
    );

    if (duplicate) {
        throw new Error(`Ошибка: бланк с серией ${duplicate.series} и номером ${duplicate.number} уже существует в этой организации.`);
    }

    const batch = await batchRepo.create({ ...item, status: 'active' });
    await materializeBatch(batch.id);
    return batch;
};
export const getBlanks = async () => (await blankRepo.list({ pageSize: 10000 })).data;

export const materializeBatch = async (batchId: string) => {
    const batch = await batchRepo.getById(batchId);
    if (!batch) throw new Error('Batch not found');
    let count = 0;
    for (let i = batch.startNumber; i <= batch.endNumber; i++) {
        await blankRepo.create({
            organizationId: batch.organizationId,
            batchId: batch.id,
            series: batch.series,
            number: i,
            status: 'available',
            ownerEmployeeId: null,
            version: 1,
            updatedAt: new Date().toISOString(),
            updatedBy: 'system'
        });
        count++;
    }
    return { created: count };
};

export const issueBlanksToDriver = async (params: any, ctx: any) => {
    const { batchId, ownerEmployeeId, ranges } = params;
    const issued = [];
    const allBlanks = await blankRepo.list({ pageSize: 10000 });
    for (const r of ranges) {
        const targets = allBlanks.data.filter(b => b.batchId === batchId && b.number >= r.from && b.number <= r.to);
        for (const b of targets) {
            b.status = 'issued';
            b.ownerEmployeeId = ownerEmployeeId;
            await blankRepo.update(b.id, b);
            issued.push(b.id);
        }
    }
    await auditBusiness('blanks.issued', { params, ...ctx });
    return { issued, skipped: [] };
};

export const searchBlanks = async (query: any) => {
    const { series, number, status, ownerEmployeeId, ownerEmployeeIds, usedInWaybillIds, page, pageSize, sortBy, sortDir } = query;

    const all = await blankRepo.list({
        pageSize: pageSize || 50,
        page: page || 1,
        // Use passed sort params or default to Number ASC
        sortBy: sortBy || 'number',
        sortDir: sortDir || 'asc',
        predicate: (b) => {
            // Фильтр по серии (частичное совпадение)
            if (series && !b.series.toLowerCase().includes(series.toLowerCase())) return false;

            // Фильтр по номеру (частичное совпадение если строка, точное если число)
            if (number !== undefined && number !== '') {
                const numStr = String(number);
                if (!String(b.number).includes(numStr)) return false;
            }

            // Фильтр по статусу (массив или строка перевода)
            if (status) {
                if (Array.isArray(status) && status.length > 0) {
                    if (!status.includes(b.status)) return false;
                } else if (typeof status === 'string' && status.trim() !== '') {
                    if (status !== b.status) return false;
                }
            }

            // Фильтр по владельцу (единичный ID или массив ID)
            if (ownerEmployeeIds && Array.isArray(ownerEmployeeIds) && ownerEmployeeIds.length > 0) {
                if (!ownerEmployeeIds.includes(b.ownerEmployeeId || '')) return false;
            } else if (ownerEmployeeId && b.ownerEmployeeId !== ownerEmployeeId) {
                return false;
            }

            // Фильтр по ID путевых листов (массив)
            if (usedInWaybillIds && Array.isArray(usedInWaybillIds)) {
                if (usedInWaybillIds.length > 0 && !usedInWaybillIds.includes(b.usedInWaybillId)) return false;
            }

            return true;
        }
    });
    return { items: all.data, total: all.total };
};

export const spoilBlank = async (params: any, ctx: any) => {
    const all = await blankRepo.list({ pageSize: 10000 });
    const b = all.data.find(x => x.id === params.blankId);
    if (b) {
        b.status = 'spoiled';
        b.spoilReasonCode = params.reasonCode;
        b.spoilReasonNote = params.note;
        b.spoiledAt = new Date().toISOString();
        await blankRepo.update(b.id, b);
        await auditBusiness('blank.spoiled', { params, ...ctx });
    }
};

export const bulkSpoilBlanks = async (params: any, ctx: any) => {
    const spoiled: { blankId: string; series: string; number: number }[] = [];
    const skipped: { blankId: string; series: string; number: number; reason: string }[] = [];

    // Get all blanks
    const allBlanks = (await blankRepo.list({ pageSize: 10000 })).data;

    // Determine which blanks to process
    let targetBlanks: WaybillBlank[] = [];

    if (params.kind === 'ids') {
        // Direct list of IDs
        const idSet = new Set(params.blankIds || []);
        targetBlanks = allBlanks.filter(b => idSet.has(b.id));
    } else if (params.kind === 'filter') {
        // Filter-based selection
        const filter = params.filter || {};
        const excludedSet = new Set(params.excludedIds || []);

        targetBlanks = allBlanks.filter(b => {
            // Apply exclusion list
            if (excludedSet.has(b.id)) return false;

            // Apply series filter (partial match)
            if (filter.series && !b.series.toLowerCase().includes(filter.series.toLowerCase())) return false;

            // Apply status filter
            if (filter.status && b.status !== filter.status) return false;

            // Apply owner filter
            if (filter.ownerName) {
                // Note: We'd need employee lookup here. For simplicity, skip owner filter in bulk.
            }

            return true;
        }).slice(0, 2000); // Apply 2000 limit
    }

    // If dry run, just count without modifying
    if (params.dryRun) {
        for (const b of targetBlanks) {
            // Check if can be spoiled
            if (b.status === 'available' || b.status === 'issued') {
                spoiled.push({ blankId: b.id, series: b.series, number: b.number });
            } else {
                skipped.push({ blankId: b.id, series: b.series, number: b.number, reason: `Статус '${b.status}' не позволяет списать` });
            }
        }
    } else {
        // Actually spoil the blanks
        for (const b of targetBlanks) {
            if (b.status === 'available' || b.status === 'issued') {
                b.status = 'spoiled';
                b.spoilReasonCode = params.reasonCode || 'other';
                b.spoilReasonNote = params.note || 'Массовое списание';
                b.spoiledAt = new Date().toISOString();
                await blankRepo.update(b.id, b);
                spoiled.push({ blankId: b.id, series: b.series, number: b.number });
            } else {
                skipped.push({ blankId: b.id, series: b.series, number: b.number, reason: `Статус '${b.status}' не позволяет списать` });
            }
        }

        // Audit if actually changed
        if (spoiled.length > 0) {
            await auditBusiness('blanks.spoiled', {
                count: spoiled.length,
                reasonCode: params.reasonCode,
                note: params.note,
                ...ctx
            });
        }
    }

    return { spoiled, skipped };
};

export const countBlanksByFilter = async (filter: any) => {
    const allBlanks = (await blankRepo.list({ pageSize: 10000 })).data;

    return allBlanks.filter(b => {
        // Apply series filter (partial match)
        if (filter.series && !b.series.toLowerCase().includes(filter.series.toLowerCase())) return false;

        // Apply status filter
        if (filter.status && b.status !== filter.status) return false;

        // Only count blanks that CAN be spoiled
        if (b.status !== 'available' && b.status !== 'issued') return false;

        return true;
    }).length;
};

export const getNextBlankForDriver = async (driverId: string, orgId: string) => {
    const all = (await blankRepo.list({ pageSize: 10000 })).data;
    // Find first issued blank
    // Sort by series then number to ensure sequential usage
    const driverBlanks = all.filter(b => b.ownerEmployeeId === driverId && b.status === 'issued');
    driverBlanks.sort((a, b) => a.series.localeCompare(b.series) || a.number - b.number);

    return driverBlanks[0] || null;
};

export const reserveBlank = async (blankId: string, waybillId: string) => {
    const b = await blankRepo.getById(blankId);
    if (b && b.status === 'issued') {
        b.status = 'reserved';
        b.reservedByWaybillId = waybillId;
        b.reservedAt = new Date().toISOString();
        await blankRepo.update(b.id, b);
    }
};

export const useBlankForWaybill = async (blankId: string, waybillId: string) => {
    const b = await blankRepo.getById(blankId);
    if (b) {
        b.status = 'used';
        b.usedInWaybillId = waybillId;
        await blankRepo.update(b.id, b);
    }
};

export const releaseBlank = async (blankId: string) => {
    const b = await blankRepo.getById(blankId);
    if (b) {
        b.status = 'issued';
        b.usedInWaybillId = null;
        b.reservedByWaybillId = null;
        b.reservedAt = null;
        await blankRepo.update(b.id, b);
    }
};

export const markBlankAsSpoiled = async (blankId: string, reason: string = 'Отмена путевого листа') => {
    const b = await blankRepo.getById(blankId);
    if (b) {
        b.status = 'spoiled';
        b.spoilReasonCode = 'other';
        b.spoilReasonNote = reason;
        b.spoiledAt = new Date().toISOString();
        // Clear linkage to waybill as it's now spoiled independently
        b.usedInWaybillId = null;
        b.reservedByWaybillId = null;
        await blankRepo.update(b.id, b);
    }
};



// Helper for consistency
export const syncBlankStatuses = async () => {
    // CRITICAL: Use waybillRepository because waybills are migrated to idb-based storage
    // using createRepo(DB_KEYS.WAYBILLS) will return an empty list or legacy data.
    const allWaybills = await waybillRepository.getAll();
    const allBlanks = (await blankRepo.list({ pageSize: 15000 })).data; // Increased buffer

    let updatedCount = 0;

    // 0. Deduplication Phase: Remove duplicate blanks (same Org+Series+Number)
    // Priority: Used > Issued > Reserved > Spoiled > Available
    const blankGroups = new Map<string, WaybillBlank[]>();

    for (const b of allBlanks) {
        // Key: orgId|series|number
        const key = `${b.organizationId}|${b.series.toLowerCase()}|${b.number}`;
        if (!blankGroups.has(key)) {
            blankGroups.set(key, []);
        }
        blankGroups.get(key)!.push(b);
    }

    const blanksToDelete: string[] = [];

    // Check groups for duplicates
    for (const [key, group] of blankGroups.entries()) {
        if (group.length > 1) {
            // Sort by priority (descending value)
            // 4=Used, 3=Issued, 2=Reserved, 1=Spoiled, 0=Available
            const getPriority = (status: BlankStatus) => {
                if (status === 'used') return 4;
                if (status === 'issued') return 3;
                if (status === 'reserved') return 2;
                if (status === 'spoiled') return 1;
                return 0; // available or other
            };

            group.sort((a, b) => {
                const pA = getPriority(a.status);
                const pB = getPriority(b.status);
                if (pA !== pB) return pB - pA; // Higher priority first

                // Tie-breaker: keep the one with waybill linkage if statuses are same (e.g. both used)
                if (a.usedInWaybillId && !b.usedInWaybillId) return -1;
                if (!a.usedInWaybillId && b.usedInWaybillId) return 1;

                // Tie-breaker: keep the most recently updated one? Or oldest? 
                // Let's keep the one that looks "most complete" or just arbitrary first.
                // Using ID as stable sort if everything else equal
                return a.id.localeCompare(b.id);
            });

            // Keep index 0, delete the rest
            const toRemove = group.slice(1);
            toRemove.forEach(b => blanksToDelete.push(b.id));

            console.log(`[Deduplication] Found duplicates for ${key}. Keeping ${group[0].id} (${group[0].status}), deleting ${toRemove.length} others.`);
        }
    }

    if (blanksToDelete.length > 0) {
        await blankRepo.removeBulk(blanksToDelete);
        updatedCount += blanksToDelete.length;
    }

    // Refresh blanks list after deletion to proceed with sync
    const refreshedBlanks = (await blankRepo.list({ pageSize: 15000 })).data;

    // Create maps for fast lookup
    const waybillMap = new Map(allWaybills.map(wb => [wb.id, wb]));

    const parseWbNumber = (num: string) => {
        if (!num) return null;
        // Clean common document prefix symbols
        const clean = num.replace(/[№#]|No/g, '').trim();

        // Match [Anything] followed by [Separator] followed by [Digits] at the end
        // This is more robust for sequences like "ЧБ001949" or "WL-202405-000001"
        const match = clean.match(/^(.*?)[ \-\.\/]*(\d+)$/);

        if (match) {
            const series = match[1].trim();
            const number = parseInt(match[2], 10);
            if (!isNaN(number)) {
                return { series, number };
            }
        }
        return null;
    };

    // 1. Cleanup Phase: remove links that no longer match the waybill number
    for (const blank of refreshedBlanks) {
        if (blank.usedInWaybillId) {
            const wb = waybillMap.get(blank.usedInWaybillId);
            let shouldDisconnect = false;

            if (!wb) {
                // Waybill deleted
                shouldDisconnect = true;
            } else {
                const parsed = parseWbNumber(wb.number);
                if (!parsed || parsed.series !== blank.series || parsed.number !== blank.number) {
                    // Number changed and no longer matches
                    shouldDisconnect = true;
                }
            }

            if (shouldDisconnect) {
                // Return status to legitimate state: issued if owned, else available
                // IMPORTANT: don't touch spoiled blanks if they were manually marked spoiled
                if (blank.status !== 'spoiled') {
                    blank.status = blank.ownerEmployeeId ? 'issued' : 'available';
                }
                blank.usedInWaybillId = null;
                await blankRepo.update(blank.id, blank);
                updatedCount++;
            }
        }
    }

    // 2. Matching Phase: create links based on current waybill numbers
    // Note: re-list to have fresh cache after cleanup? No, we modified in-place or can just use refreshedBlanks if we are careful.
    // Ideally fetch fresh again to be safe.
    const finalBlanks = (await blankRepo.list({ pageSize: 15000 })).data;

    for (const wb of allWaybills) {
        const parsed = parseWbNumber(wb.number);
        if (parsed) {
            // Find blank by CURRENT number
            // Note: we use refreshed list so we don't accidentally link to something we just tentatively unlinked elsewhere
            const blank = finalBlanks.find(b => b.series === parsed.series && b.number === parsed.number);

            if (blank) {
                let needsUpdate = false;

                if (blank.usedInWaybillId !== wb.id) {
                    blank.usedInWaybillId = wb.id;
                    needsUpdate = true;
                }

                if (blank.status !== 'used' && blank.status !== 'spoiled') {
                    blank.status = 'used';
                    needsUpdate = true;
                }

                if (needsUpdate) {
                    await blankRepo.update(blank.id, blank);
                    updatedCount++;
                }
            }
        }
    }

    return { updated: updatedCount };
};

export const forceUpdateBlankStatus = async (blankId: string, newStatus: import('../../types').BlankStatus) => {
    const b = await blankRepo.getById(blankId);
    if (b) {
        b.status = newStatus;

        // Cleanup logic based on new status
        if (newStatus === 'available') {
            b.ownerEmployeeId = null;
            b.usedInWaybillId = null;
            b.reservedByWaybillId = null;
            b.reservedAt = null;
        } else if (newStatus === 'issued') {
            b.usedInWaybillId = null;
            b.reservedByWaybillId = null;
            b.reservedAt = null;
        } else if (newStatus === 'spoiled') {
            // Safe to clear active links
            b.usedInWaybillId = null;
            b.reservedByWaybillId = null;
            b.reservedAt = null;
            if (!b.spoilReasonNote) b.spoilReasonNote = 'Ручное изменение статуса';
        }
        // If 'reserved' or 'used', we assume the user knows what they are doing or just fixing the status label.
        // We do NOT clear IDs here because maybe they are restoring a status for an existing link.

        await blankRepo.update(b.id, b);
    }
};

export const deleteBlankBatch = async (batchId: string) => {
    const batch = await batchRepo.getById(batchId);
    if (!batch) throw new Error('Пачка не найдена'); // Batch not found

    // Check for used blanks
    const allBlanks = (await blankRepo.list({ pageSize: 10000 })).data;
    const batchBlanks = allBlanks.filter(b => b.batchId === batchId);

    const unsafeBlank = batchBlanks.find(b => b.status === 'used' || b.status === 'reserved' || b.status === 'spoiled');
    if (unsafeBlank) {
        throw new Error('Нельзя удалить пачку, содержащую использованные, зарезервированные или списанные бланки.'); // Cannot delete batch with used/reserved/spoiled blanks
    }

    // Safe to delete
    // 1. Delete all blanks in batch
    if (batchBlanks.length > 0) {
        await blankRepo.removeBulk(batchBlanks.map(b => b.id));
    }
    // 2. Delete batch
    await batchRepo.remove(batchId);
};

export const updateBlankBatch = async (id: string, data: Partial<WaybillBlankBatch>) => {
    const batch = await batchRepo.getById(id);
    if (!batch) throw new Error('Пачка не найдена');

    // Prevent changing critical fields if blanks are generated (materialized)
    const allBlanks = (await blankRepo.list({ pageSize: 1 })).data; // Just check if any exist? No, need check for THIS batch.
    // Optimization: check 'isMaterialized' equivalent by checking if any blank exists for this batch
    // Actually we can list with filter if repo supports it, but here we list all often.
    // Let's reuse existing patterns.
    const hasBlanks = (await blankRepo.list({ pageSize: 10000 })).data.some(b => b.batchId === id);

    if (hasBlanks) {
        if (data.startNumber !== undefined && data.startNumber !== batch.startNumber) {
            throw new Error('Нельзя изменить начальный номер уже материализованной пачки.');
        }
        if (data.endNumber !== undefined && data.endNumber !== batch.endNumber) {
            throw new Error('Нельзя изменить конечный номер уже материализованной пачки.');
        }
    }

    const updatedBatch = { ...batch, ...data, updatedAt: new Date().toISOString() };
    await batchRepo.update(id, updatedBatch);
    return updatedBatch;
};
